// js/utils/theme-helpers.ts

/**
 * Defines a theme configuration simple callable API
 *
 * @example
 * const themes = defineTheme({
 *   vertical: 'metadata--vertical',
 *   compact: 'metadata--compact'
 * });
 *
 * // Callable (returns className)
 * <div className={cn('base', themes(theme))} />
 *
 * // Direct access (for comparisons)
 * if (theme === themes.vertical) { ... }
 *
 * // Type guard
 * if (themes.has(backendValue)) { ... }
 */
export function defineTheme<T extends Record<string, string>>(config: T) {
  const keys = Object.keys(config);

  // Create the callable function
  const themeFn = (key?: keyof T | (keyof T)[]): string => {
    if (!key) return '';

    const keyArray = Array.isArray(key) ? key : [key];
    const validClasses: string[] = [];

    for (const k of keyArray) {
      if (keys.includes(k as string)) {
        validClasses.push(config[k]);
      } else if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          `Invalid theme key '${String(k)}'${Array.isArray(key) ? ' in array' : ''}. Valid keys: ${keys.join(', ')}`
        );
      }
    }

    // Dedupe and join
    return [...new Set(validClasses)].join(' ');
  };

  // Add config properties and has method
  return Object.assign(themeFn, {
    ...config,
    has(key: unknown): key is keyof T {
      return typeof key === 'string' && keys.includes(key);
    }
  });
}

/**
 * Extract theme keys as a union type
 */
export type ThemeKeys<THelper> = Exclude<keyof THelper, 'has'>;

/**
 * Check if a theme value contains a specific theme key
 */
export function hasTheme<T extends string>(value: T | readonly T[] | undefined, target: T): value is T | T[] {
  if (!value) return false;
  const arr = Array.isArray(value) ? value : [value];
  return arr.includes(target);
}
